/**
 * The add-column gallery (spec M1-B4 §8.2 H5; **measured at last in 6i**, q453 / Q166).
 *
 * Round 1's probe landed on the help-centre container, so round 3 shipped grouped lists. The
 * gallery was in fact already in 6f's captures — monday pre-mounts it **off-screen at x 5133**
 * — and 6i's `add-column-gallery` step opened it in place. Measured
 * (`[c:add-column-gallery]`, `[c:item-context]`):
 *
 *   wrapper 373 × 485 → `ds-menu-inner` **341 × 469** at +16/+16
 *   `search-section`      341 × 48   (a 295 × 22 input)
 *   `essential-section`   341 × 130  — heading `Essentials` 12 px/400, then rows
 *   rows                  **159 × 32**, two columns at +0 / +167 (an 8 px gutter), 32 px pitch
 *   second section        heading `Super useful`, four more row-pairs
 *   footer `More columns` 341 × 32
 *
 * The DOM order is **column-major** (Status · Text · People, then Dropdown · Date · Numbers),
 * so the grid *reads* Status | Dropdown / Text | Date / People | Numbers — which is what
 * `grid-auto-flow: column` reproduces from a single flat list.
 *
 * monday's 14 top-level types are Status · Text · People · Dropdown · Date · Numbers ⎮ Files ·
 * monday Doc · Connect boards · Timeline · Checkbox · Formula · Extract info · Priority. Three
 * of them — **monday Doc**, **Extract info** (AI) and **Priority** (a status preset) — are not
 * registered column types in this product, and the §3.2 rule the rest of the shell follows is
 * that a monday-only row is absent rather than faked. The other eleven are here, in monday's
 * order. Everything else the registry has is one click away under `More columns`, which on
 * monday opens the **Column Center** (`[c:add-column-more]`).
 */
import { Icon, icons } from '@elizmonday/ui';
import { useMemo, useState, type CSSProperties } from 'react';
import { galleryEntries } from '../../columns/registry';
import { useTableActions } from '../context';
import { ColumnCenter } from './ColumnCenter';
import { Menu } from './Menu';

/** `ds-menu-inner` is 341 wide; `Menu` adds its own 8 px padding either side. */
export const ADD_COLUMN_GALLERY_WIDTH = 357;

/**
 * monday's two top-level sections and their order, by our registry's type ids. A type monday
 * lists that this product does not register is simply not in the array (see the header).
 */
const FEATURED: readonly { heading: string; types: readonly string[] }[] = [
  { heading: 'Essentials', types: ['status', 'text', 'people', 'dropdown', 'date', 'numbers'] },
  // monday's `Super useful`, less monday Doc · Extract info · Priority.
  { heading: 'Super useful', types: ['file', 'board_relation', 'timeline', 'checkbox', 'formula'] },
];

/**
 * monday's grid is column-major, so the CSS needs the **row** count: with `grid-auto-flow:
 * column` a 6-entry section is 3 rows of 2, which is exactly what was measured.
 */
function gridRows(count: number): CSSProperties {
  return { ['--ez-addcol-rows' as string]: String(Math.max(1, Math.ceil(count / 2))) };
}

/**
 * The gallery is opened from the grid's `+` and from the column menu's `Add column to the
 * right`, neither of which carries a coordinate, so it used to render at (0, 0) — over the
 * sidebar. It anchors under the `+` when one is on screen, and falls back to the caller's
 * coordinate; `Menu` clamps whatever it gets into the viewport (P-005).
 *
 * Round 7a (P-007): the last resort is **(0, 0) again**, not an invented (120, 160). `Menu`
 * now reads (0, 0) as *"no coordinate"* and anchors to the control that opened it, which is
 * right in both cases — the `+` and the column menu's row — and is one rule instead of two.
 */
function anchorOf(x: number, y: number): { x: number; y: number } {
  if (x !== 0 || y !== 0) return { x, y };
  const plus = document.querySelector('[data-testid="add-column"]');
  if (!plus) return { x: 0, y: 0 };
  const box = plus.getBoundingClientRect();
  if (box.width === 0 && box.height === 0) return { x: 0, y: 0 };
  return { x: Math.round(box.left), y: Math.round(box.bottom + 4) };
}

export function AddColumnGallery({ x, y }: { x: number; y: number }) {
  const actions = useTableActions();
  const [anchor] = useState(() => anchorOf(x, y));
  const [query, setQuery] = useState('');
  const [center, setCenter] = useState(false);

  const all = useMemo(() => galleryEntries(), []);
  const byType = useMemo(() => new Map(all.map((entry) => [entry.type, entry])), [all]);

  /**
   * Searching leaves monday's two sections behind and grids **every** registered type that
   * matches, because a user who types "rating" wants the rating column, not `More columns`.
   */
  const needle = query.trim().toLowerCase();
  const matches = useMemo(
    () =>
      needle === ''
        ? []
        : all.filter(
            (entry) =>
              entry.title.toLowerCase().includes(needle) ||
              entry.keywords.some((keyword) => keyword.includes(needle)),
          ),
    [all, needle],
  );

  const close = () => actions.openPane(null);

  if (center) {
    return <ColumnCenter onClose={close} initialQuery={query} />;
  }

  const entryButton = (entry: (typeof all)[number]) => (
    <button
      key={entry.type}
      type="button"
      role="menuitem"
      className="ez-menu__item ez-addcol__entry"
      data-testid={`gallery-${entry.type}`}
      title={entry.description}
      onClick={() => void actions.addColumn(entry.type, entry.title)}
    >
      <Icon icon={icons[entry.icon]} size={16} ignoreFocusStyle />
      <span>{entry.title}</span>
    </button>
  );

  return (
    <Menu
      x={anchor.x}
      y={anchor.y}
      width={ADD_COLUMN_GALLERY_WIDTH}
      label="Add column"
      onClose={close}
    >
      <div className="ez-addcol" data-testid="add-column-gallery">
        <input
          className="ez-addcol__search"
          type="search"
          autoFocus
          aria-label="Search column types"
          placeholder="Search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />

        {needle === '' ? (
          FEATURED.map((section) => {
            const entries = section.types
              .map((type) => byType.get(type))
              .filter((entry): entry is (typeof all)[number] => entry !== undefined);
            if (entries.length === 0) return null;
            return (
              <section key={section.heading} className="ez-addcol__section">
                <h3 className="ez-addcol__title">{section.heading}</h3>
                <div className="ez-addcol__grid" style={gridRows(entries.length)}>
                  {entries.map(entryButton)}
                </div>
              </section>
            );
          })
        ) : matches.length === 0 ? (
          <p className="ez-addcol__empty">No column type matches</p>
        ) : (
          <div className="ez-addcol__grid" style={gridRows(matches.length)}>
            {matches.map(entryButton)}
          </div>
        )}

        <button
          type="button"
          role="menuitem"
          className="ez-menu__item ez-addcol__more"
          data-testid="gallery-more-columns"
          onClick={() => setCenter(true)}
        >
          More columns
        </button>
      </div>
    </Menu>
  );
}
